/**
 * MKS-393 — the /profile nudge banner.
 *
 * Shows only when ALL SIX merch fields are empty, and only until the person
 * dismisses it. Two conditions, two sources, deliberately:
 *
 *   - "all six empty" is read from the COLUMNS, so filling anything in hides
 *     the banner on the next render whether or not it was ever dismissed;
 *   - the dismissal is `merchSizesNudgeDismissed` on the existing preferences
 *     JSONB, so it follows the person across browsers rather than living in
 *     localStorage on one laptop.
 *
 * Absent preference = not dismissed, which is what makes this show for
 * everyone who has not seen it rather than for nobody.
 */
import { XMarkIcon } from '@heroicons/react/24/outline';
import { usePreferences, useUpdatePreferences } from '../../hooks/usePreferences';
import { usePersonalDetails } from '../../hooks/useMerchSizes';
import { hasNoMerchSizes } from '../../constants/merchSizes';

export function MerchSizesNudgeBanner() {
  const { data: details, isLoading: detailsLoading } = usePersonalDetails();
  const { data: preferences, isLoading: preferencesLoading } = usePreferences();
  const updatePreferences = useUpdatePreferences();

  // Never flash the banner while either half is still loading: a person who
  // already filled their sizes in should not see it for one frame.
  if (detailsLoading || preferencesLoading) return null;
  if (preferences?.merchSizesNudgeDismissed === true) return null;
  if (!hasNoMerchSizes(details)) return null;

  return (
    <div
      role="status"
      className="flex items-start justify-between gap-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3"
    >
      <p className="text-sm text-amber-900">
        We order company swag a few times a year — fill in your sizes so we can include you.
      </p>
      <button
        type="button"
        aria-label="Dismiss the merch sizes reminder"
        className="shrink-0 rounded p-1 text-amber-700 hover:bg-amber-100"
        onClick={() => updatePreferences.mutate({ merchSizesNudgeDismissed: true })}
      >
        <XMarkIcon className="h-4 w-4" />
      </button>
    </div>
  );
}
